// Кнопка звука в правом верхнем углу поля.
//
// Одно нажатие глушит игру, второе возвращает звук. Другого выбора у игрока нет: меню настроек
// в игре не собрано, см. ADR-26.
//
// Кадра художник дал два, по одному на каждое состояние. Наведение и нажатие берут тот же кадр:
// отвечает кнопка сменой значка, а он меняется на каждом нажатии, см. ADR-47.
//
// Стоит она в углу макета, а не экрана, и на том же отступе, что кнопка спина, см. EDGE_MARGIN.

import { Container, type Texture } from 'pixi.js';

import type { UiFrame } from '../assets/catalog';
import type { TextureSheet } from '../assets/manager';
import { gameSound } from '../sound/sound';

import { RoundButton, type ButtonFaces } from './button';
import { minTouchableScale, type GameLayout, viewportEdge } from './layout';
import { DESIGN_HEIGHT, DESIGN_WIDTH, EDGE_MARGIN } from './scene';

/** Один кадр на все виды кнопки: своих кадров наведения и нажатия у значка нет. */
function faces(texture: Texture): ButtonFaces {
  return { idle: texture, hover: texture, active: texture };
}

/** Кнопка звука: она глушит игру и возвращает звук обратно. */
export class SoundButton {
  readonly view: Container = new Container();

  readonly #button: RoundButton;
  readonly #on: ButtonFaces;
  readonly #off: ButtonFaces;

  constructor(ui: TextureSheet<UiFrame>) {
    this.#on = faces(ui.soundOn);
    this.#off = faces(ui.soundOff);

    this.#button = new RoundButton(this.#on, () => this.#toggle());
    this.#button.view.position.set(DESIGN_WIDTH / 2 - EDGE_MARGIN - this.#button.radius, -DESIGN_HEIGHT / 2 + EDGE_MARGIN + this.#button.radius);

    // Мьют игрок мог поставить в прошлый заход: значок начинает с того, чем игрок кончил.
    this.#draw();

    this.view.addChild(this.#button.view);
  }

  /** Двигает затухание кнопки. */
  update(seconds: number): void {
    this.#button.update(seconds);
  }

  layout(layout: GameLayout): void {
    const { bounds, composition, viewport } = layout;
    const mobile: boolean = composition === 'mobile';
    const scale: number = mobile ? minTouchableScale(viewport, this.#button.radius, 22) : 1;
    const radius: number = this.#button.radius * scale;
    const edge: number = mobile ? viewportEdge(viewport) : EDGE_MARGIN;
    this.#button.view.scale.set(scale);
    this.#button.view.position.set(
      bounds.right - edge - radius,
      bounds.top + edge + radius,
    );
  }

  #toggle(): void {
    const sound = gameSound();

    sound.muted = !sound.muted;
    this.#draw();
  }

  /** Ставит значок по тому, звучит игра или молчит. */
  #draw(): void {
    this.#button.faces = gameSound().muted ? this.#off : this.#on;
  }
}
